import React from 'react';
import PropTypes from 'prop-types';
import { LuFileText, LuHash } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import { getInitials } from '../../../components/ui/Avatar';
import { useTranslation } from '../../../i18n/LanguageContext';
import {
  CUSTOMER_COLUMNS,
  displayValue,
  formatIsoDate,
  formatPaymentPeriod,
  getFilterValueLabel,
  isEmptyValue,
} from '../customerColumns';

/** Two letters for a company name ("ABC İLAÇ A.Ş." → "Aİ"). */
export const customerInitials = (name) => {
  const words = String(name || '').split(/\s+/).filter((word) => /[\p{L}\p{N}]/u.test(word));
  return getInitials(words[0], words[1]);
};

/** Plain-text form of a cell, for its tooltip. */
export const getCellTitle = (customer, key) => {
  const value = customer[key];
  if (key === 'Durum' || value === undefined || value === null || typeof value === 'object') return undefined;
  return String(value);
};

/** Durum: aktif / inaktif; any other stored text is shown as it is. */
export const StatusBadge = ({ value }) => {
  const { t } = useTranslation();
  if (value === undefined || value === null || value === '') return <span className="tw-text-slate-400">-</span>;
  const status = String(value).toLowerCase();
  if (status === 'aktif') {
    return (
      <Badge tone="success">
        <span aria-hidden="true" className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-emerald-500" />
        {t('customers.status.active')}
      </Badge>
    );
  }
  return (
    <Badge tone="neutral">
      <span aria-hidden="true" className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-slate-400" />
      {status === 'inaktif' ? t('customers.status.inactive') : String(value)}
    </Badge>
  );
};

StatusBadge.propTypes = { value: PropTypes.string };

/** Content of one customer table cell. */
const CustomerCell = ({ columnKey, customer }) => {
  const { t, locale } = useTranslation();
  const value = customer[columnKey];
  const column = CUSTOMER_COLUMNS[columnKey];
  const muted = <span className="tw-text-slate-400 dark:tw-text-slate-500">-</span>;

  if (columnKey === 'Başlık') {
    return (
      <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
        <span
          aria-hidden="true"
          className="tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-indigo-400/90 tw-to-violet-600/90 tw-text-[10px] tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]"
        >
          {customerInitials(value)}
        </span>
        <span className="tw-min-w-0 tw-truncate tw-font-semibold tw-text-slate-900 dark:tw-text-white">{displayValue(value)}</span>
      </span>
    );
  }

  if (columnKey === 'Durum') return <StatusBadge value={isEmptyValue(value) ? undefined : String(value)} />;
  if (isEmptyValue(value)) return muted;
  if (columnKey === 'Ödeme Periyodu') return <span className="tw-block tw-truncate">{formatPaymentPeriod(t, value)}</span>;
  if (column.type === 'date') return <span className="tw-block tw-truncate tw-tabular-nums">{formatIsoDate(value, locale)}</span>;
  if (columnKey === 'Döviz Tipi') {
    return <span className="tw-rounded-md tw-bg-slate-900/[0.05] tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-tracking-wide tw-text-slate-600 dark:tw-bg-white/[0.07] dark:tw-text-slate-300">{String(value)}</span>;
  }
  if (column.type === 'select') return <span className="tw-block tw-truncate">{getFilterValueLabel(t, columnKey, value)}</span>;
  if (columnKey === 'Vergi Numarası' || columnKey === 'Ana Anlaşma Numarası') {
    return <span className="tw-block tw-truncate tw-tabular-nums">{String(value)}</span>;
  }
  return <span className="tw-block tw-truncate">{String(value)}</span>;
};

CustomerCell.propTypes = {
  columnKey: PropTypes.string.isRequired,
  customer: PropTypes.object.isRequired,
};

/** One row of the card list (narrow screens): name, info, tax no and status. */
export const CustomerCardRow = ({ customer }) => {
  const name = displayValue(customer['Başlık']);
  const info = isEmptyValue(customer['Bilgi']) ? '' : String(customer['Bilgi']);
  const taxNumber = isEmptyValue(customer['Vergi Numarası']) ? '' : String(customer['Vergi Numarası']);
  const agreement = isEmptyValue(customer['Ana Anlaşma Numarası']) ? '' : String(customer['Ana Anlaşma Numarası']);

  return (
    <>
      <span
        aria-hidden="true"
        className="tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-indigo-400 tw-to-violet-600 tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]"
      >
        {customerInitials(customer['Başlık'])}
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-block tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{name}</span>
        {info && <span className="tw-mt-0.5 tw-block tw-truncate tw-text-[13px] tw-text-slate-500 dark:tw-text-slate-400">{info}</span>}
        {(taxNumber || agreement) && (
          <span className="tw-mt-1.5 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
            {taxNumber && (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-tabular-nums">
                <LuHash aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" />
                {taxNumber}
              </span>
            )}
            {agreement && (
              <span className="tw-inline-flex tw-min-w-0 tw-max-w-full tw-items-center tw-gap-1">
                <LuFileText aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" />
                <span className="tw-truncate">{agreement}</span>
              </span>
            )}
          </span>
        )}
      </span>
      <span className="tw-shrink-0">
        <StatusBadge value={isEmptyValue(customer['Durum']) ? undefined : String(customer['Durum'])} />
      </span>
    </>
  );
};

CustomerCardRow.propTypes = { customer: PropTypes.object.isRequired };

export default CustomerCell;
